import type { Article, NeoArticleView, NeoArticleClassification, SimpleArticleDocument, ArtikelGaranEntry } from '@expert/types';
import { buildArticleLinkFromHit } from '~/composables/ExpApi/expArticle';
import { mapToExpMedium } from '~/composables/ExpApi/expSearch';

// mapArticleFromHit
export const mapFromHit = (hit: SimpleArticleDocument): Article => {
    const article: Article = mapFromExpArticleView(hit.articleView, hit.slug);
    article.link = buildArticleLinkFromHit(hit);
    if (hit?.primaryImageData && article?.primaryImage) {
        try {
            mapToExpMedium(hit.primaryImageData, article.primaryImage);
        } catch {
            // ignore
        }
    }
    return article;
};

export const mapFromExpArticleView = (articleView: NeoArticleView | undefined, slug: string): Article => {
    const mappedArticle: Article = {} as Article;

    if (articleView?.contextSensitiveData) {
        mappedArticle.descriptionList = articleView.contextSensitiveData.priceTagText;
        mappedArticle.description = articleView.contextSensitiveData?.description;
        mappedArticle.headline = articleView.contextSensitiveData.headline;
        mappedArticle.formerPriceState = articleView.contextSensitiveData.formerPriceState;
        mappedArticle.primaryImage = articleView.contextSensitiveData.primaryImage;
        mappedArticle.imageGallery = articleView.contextSensitiveData.gallery;
        mappedArticle.documentList = articleView.contextSensitiveData.downloadDocuments;
        mappedArticle.title = articleView.contextSensitiveData.title;
        mappedArticle.seoDescription = articleView.contextSensitiveData.seoTitle;
        mappedArticle.seoPageTitle = articleView.contextSensitiveData.pageTitle;
        mappedArticle.shortName = articleView.contextSensitiveData.shortName;
        mappedArticle.searchKeywords = articleView.contextSensitiveData.searchKeywords;
        mappedArticle.name = articleView.contextSensitiveData.name;
        mappedArticle.primaryCategory = articleView.contextSensitiveData.primaryCategory;
        mappedArticle.categories = articleView.contextSensitiveData.categories;
        mappedArticle.crossSaleArticles = articleView.contextSensitiveData.crossSaleArticles;
        mappedArticle.additionalArticles = articleView.contextSensitiveData.additionalArticles;
        mappedArticle.active = articleView.contextSensitiveData.active;
        mappedArticle.scopeOfDelivery = articleView.contextSensitiveData.packageContents;
        mappedArticle.nameSubscript = articleView.contextSensitiveData.nameSubscript;
        mappedArticle.testResults = articleView.contextSensitiveData.testScores ?? [];
        if (articleView.contextSensitiveData.formerPrice) {
            mappedArticle.formerNettoPrice = articleView.contextSensitiveData.formerPrice.net;
            mappedArticle.formerBruttoPrice = articleView.contextSensitiveData.formerPrice.gross;
            mappedArticle.formerCurrency = articleView.contextSensitiveData.formerPrice.currency;
            mappedArticle.formerTaxRate = articleView.contextSensitiveData.formerPrice.taxRate;
        }
    }

    if (articleView?.articleProperties) {
        mappedArticle.variationId = articleView.articleProperties.variationId;
        mappedArticle.featureLogos = articleView.articleProperties.featureLogos;
        mappedArticle.productType = articleView.articleProperties.productType;
        mappedArticle.webcode = articleView.articleProperties.articleNumber;
        mappedArticle.internationalArticleNumber = articleView.articleProperties.internationalArticleNumber;
        mappedArticle.brandId = articleView.articleProperties.brand;
        mappedArticle.visibility = articleView.articleProperties.visibility;
        mappedArticle.manufacturerPartNumber = articleView.articleProperties.manufacturerPartNumber;
        mappedArticle.featureAttributes = articleView.articleProperties.featureAttributes;
        mappedArticle.earn = articleView.articleProperties?.paybackArticle?.earn ?? false;
        if (articleView.articleProperties.classification?.values?.['@arrayMap']) {
            mappedArticle.technicalData = articleView.articleProperties.classification.values['@arrayMap'];
            const classificationDocuments = getDocumentsFromClassifications(
                articleView.articleProperties.classification,
            );
            if (classificationDocuments.length > 0) {
                mappedArticle.documentList = mappedArticle.documentList.concat(classificationDocuments);
            }
        }
    }

    const artikelGaran = articleView?.contextSensitiveData?.artikelGaran;
    if (Array.isArray(artikelGaran) && artikelGaran.length > 0) {
        mappedArticle.artikelGaran = artikelGaran as Array<ArtikelGaranEntry>;
    }

    mappedArticle.articleId = articleView?.articleId ?? '';
    mappedArticle.variationId = articleView?.articleProperties?.variationId ?? 'GrailsInMeap';
    mappedArticle.slug = slug;

    return mappedArticle;
};

const getDocumentsFromClassifications = (classifications: NeoArticleClassification): Array<string> => {
    return classifications?.values['@arrayMap']
        ?.filter((technicalData) => {
            return technicalData.key === '59ba44f5a7b11b000152d48a';
        })
        .map((technicalData) => {
            return technicalData.value.value;
        });
};
